import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, FirestoreError, Timestamp } from 'firebase/firestore';
import { DEFAULT_USER_SETTINGS } from './constants';

import { auth, db } from './lib/firebase';
import {
  firebaseUserAtom,
  isLoadingAuthAtom,
  userAtom,
  isLoadingUserProfileAtom,
  themeAtom,
} from './stores/auth';
import { User, UserSettings } from './types';

// Pages
import { DailyView } from './pages/DailyView';
import { ShutdownRitualPage } from './pages/ShutdownRitualPage';
import { WeeklyPlanningPage } from './pages/WeeklyPlanningPage';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { DailyViewSkeleton } from './pages/DailyViewSkeleton';
import { openPanelAtom, closeAllPanelsAtom } from './stores/panels';

// Components
import { PWAInstallPrompt } from './components/PWAInstallPrompt';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const isLoading = useAtomValue(isLoadingAuthAtom);

  if (isLoading) {
    if (location.pathname === '/') {
      return <DailyViewSkeleton />;
    }

    return <div className="loading-screen" aria-busy="true" />;
  }

  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const isLoading = useAtomValue(isLoadingAuthAtom);

  if (isLoading) {
    return <div className="loading-screen" aria-busy="true" />;
  }

  if (firebaseUser) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function SettingsRoute() {
  const openPanel = useSetAtom(openPanelAtom);
  const navigate = useNavigate();

  useEffect(() => {
    openPanel('settings');
    navigate('/', { replace: true });
  }, [openPanel, navigate]);

  return null;
}

export default function App() {
  const setFirebaseUser = useSetAtom(firebaseUserAtom);
  const setUser = useSetAtom(userAtom);
  const setIsLoadingUserProfile = useSetAtom(isLoadingUserProfileAtom);
  const closeAllPanels = useSetAtom(closeAllPanelsAtom);
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const theme = useAtomValue(themeAtom);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);

      // When user logs out, clear all app state
      if (user === null) {
        setUser(null);
        closeAllPanels();
        setIsLoadingUserProfile(false);
      }
    });

    return () => unsubscribe();
  }, [setFirebaseUser, setUser, closeAllPanels, setIsLoadingUserProfile]);

  // Fetch user profile from Firestore when Firebase user changes
  useEffect(() => {
    async function fetchUserProfile() {
      if (firebaseUser === undefined) {
        // Still loading auth state, do nothing
        return;
      }

      if (firebaseUser === null) {
        // User is not authenticated, clear user profile
        setUser(null);
        setIsLoadingUserProfile(false);
        return;
      }

      // User is authenticated, fetch their profile
      setIsLoadingUserProfile(true);
      try {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          setUser(userDoc.data() as User);
        } else {
          // User profile doesn't exist - create a default one
          // This handles edge cases like signup flow failures or deleted profiles
          console.warn('User profile not found - creating default profile');
          const now = Timestamp.now();
          const defaultProfile: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || 'User',
            settings: DEFAULT_USER_SETTINGS as UserSettings,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
            subscriptionTier: 'free',
            createdAt: now,
            updatedAt: now,
          };

          try {
            await setDoc(userDocRef, defaultProfile);
            setUser(defaultProfile);
          } catch (createError) {
            console.error('Failed to create default profile:', createError);
            // If we can't create a profile, force logout
            await signOut(auth);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);

        // If we get a permission error, the auth token is likely invalid/expired
        // Force logout to reset the auth state
        const firestoreError = error as FirestoreError;
        if (firestoreError.code === 'permission-denied') {
          console.warn('Permission denied - forcing logout to reset auth state');
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('Failed to force logout:', signOutError);
          }
        }

        setUser(null);
      } finally {
        setIsLoadingUserProfile(false);
      }
    }

    fetchUserProfile();
  }, [firebaseUser, setUser, setIsLoadingUserProfile]);

  // Apply theme from user settings
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <>
      <Routes>
        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DailyView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shutdown"
          element={
            <ProtectedRoute>
              <ShutdownRitualPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/weekly"
          element={
            <ProtectedRoute>
              <WeeklyPlanningPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Paper texture overlay */}
      <div className="paper-texture" aria-hidden="true" />
    </>
  );
}
